import { ArrowLeft, Award, BookOpen, Users, Shield, Heart, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router';
import { Card } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { useSEO, COMPETITIVE_KEYWORD_CLUSTERS, mergeKeywordSets } from '../lib/seo';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';

export default function About() {
  const navigate = useNavigate();
  useSEO({
    title: 'About TENSION | Relationship Intelligence Platform',
    description:
      'Learn how TENSION combines relationship science, conflict prevention frameworks, and practical coaching to help couples communicate better and repair faster.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.core as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.communication as unknown as string[],
      ['relationship science app', 'attachment theory app', 'gottman-informed relationship tools'],
    ),
    canonical: '/about',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  return (
    <WarmMarketingBackdrop>
      <div className="mx-auto max-w-4xl px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          aria-label="Go back"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <StockHeroStrip
          stockKey="supportCalm"
          alt="Calm, modern support context with high-trust relationship guidance"
        />
        <div className="space-y-8">
          {/* Hero */}
          <div className="space-y-4 py-6 text-center">
            <h1 className="text-4xl font-light tracking-wide text-neutral-900">About TENSION</h1>
            <p className="mx-auto max-w-2xl text-xl text-neutral-600">
              A science-backed approach to measuring relationship tension before it causes emotional distance
            </p>
          </div>

          {/* Mission */}
          <Card className="border-violet-200/90 bg-white/95 p-8 shadow-xl shadow-violet-900/15">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 rounded-full bg-blue-100">
                <Heart className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-medium mb-3">Our Mission</h2>
                <p className="text-neutral-700 leading-relaxed">
                  Most relationship problems escalate invisibly. Small tensions compound into resentment,
                  over-availability erodes attraction, and withdrawal creates distance—all before either
                  partner realizes what's happening.
                </p>
                <p className="text-neutral-700 leading-relaxed mt-4">
                  TENSION makes the invisible visible. We provide a structured, evidence-based framework
                  for measuring and forecasting relationship tension before it causes lasting damage.
                </p>
              </div>
            </div>
          </Card>

          {/* The Science */}
          <Card className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 rounded-full bg-purple-100">
                <BookOpen className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-2xl font-medium mb-3">The Science Behind TENSION</h2>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  Our tension score (RTI) is built on decades of research in attachment theory,
                  interpersonal neurobiology, and relationship dynamics:
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-neutral-50 rounded-lg">
                <h3 className="font-medium mb-2">Attachment Theory</h3>
                <p className="text-sm text-neutral-600">
                  Developed by John Bowlby and Mary Ainsworth, attachment theory explains how our early
                  bonding experiences shape relationship patterns throughout life.
                </p>
                <p className="text-xs text-neutral-500 mt-2">
                  Bowlby, J. (1988). A Secure Base: Parent-Child Attachment and Healthy Human Development
                </p>
              </div>

              <div className="p-4 bg-neutral-50 rounded-lg">
                <h3 className="font-medium mb-2">Gottman Method</h3>
                <p className="text-sm text-neutral-600">
                  Dr. John Gottman's 40+ years of research identified behavioral patterns that predict
                  relationship success or failure with 90%+ accuracy.
                </p>
                <p className="text-xs text-neutral-500 mt-2">
                  Gottman, J. M., & Silver, N. (1999). The Seven Principles for Making Marriage Work
                </p>
              </div>

              <div className="p-4 bg-neutral-50 rounded-lg">
                <h3 className="font-medium mb-2">Polyvagal Theory</h3>
                <p className="text-sm text-neutral-600">
                  Stephen Porges' work on the autonomic nervous system explains how we respond to
                  relationship stress through fight, flight, freeze, or fawn responses.
                </p>
                <p className="text-xs text-neutral-500 mt-2">
                  Porges, S. W. (2011). The Polyvagal Theory
                </p>
              </div>

              <div className="p-4 bg-neutral-50 rounded-lg">
                <h3 className="font-medium mb-2">Interdependence Theory</h3>
                <p className="text-sm text-neutral-600">
                  Research on relationship dynamics, including initiation patterns, responsiveness,
                  and the balance of closeness and autonomy.
                </p>
                <p className="text-xs text-neutral-500 mt-2">
                  Rusbult, C. E., & Van Lange, P. A. (2003). Interdependence, Interaction, and Relationships
                </p>
              </div>
            </div>
          </Card>

          {/* The tension score framework */}
          <Card className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 rounded-full bg-green-100">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-medium mb-3">The Tension Score Framework</h2>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  The tension score (RTI) measures five core behavioral components:
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4 py-2">
                <h4 className="font-medium">1. Initiation Imbalance</h4>
                <p className="text-sm text-neutral-600">
                  Tracks who initiates contact, conversations, and plans. Chronic imbalance creates
                  resentment in the over-initiator and complacency in the under-initiator.
                </p>
              </div>

              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <h4 className="font-medium">2. Response Latency</h4>
                <p className="text-sm text-neutral-600">
                  Measures delays in responding to messages and emotional bids. Delayed responsiveness
                  signals disengagement and triggers anxiety in anxiously-attached partners.
                </p>
              </div>

              <div className="border-l-4 border-amber-500 pl-4 py-2">
                <h4 className="font-medium">3. Emotional Density</h4>
                <p className="text-sm text-neutral-600">
                  Assesses the intensity of emotional investment. Mismatched density creates pressure
                  for the lower-investment partner and insecurity for the higher-investment partner.
                </p>
              </div>

              <div className="border-l-4 border-red-500 pl-4 py-2">
                <h4 className="font-medium">4. Over-Availability</h4>
                <p className="text-sm text-neutral-600">
                  Detects excessive availability, over-accommodation, and loss of independence.
                  Over-availability erodes attraction and can signal anxious attachment activation.
                </p>
              </div>

              <div className="border-l-4 border-neutral-500 pl-4 py-2">
                <h4 className="font-medium">5. Withdrawal Signals</h4>
                <p className="text-sm text-neutral-600">
                  Identifies patterns of emotional or physical withdrawal, stonewalling, and avoidance.
                  Withdrawal is one of Gottman's "Four Horsemen" of relationship breakdown.
                </p>
              </div>
            </div>
          </Card>

          {/* Expert Team */}
          <Card className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 rounded-full bg-amber-100">
                <Award className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-2xl font-medium mb-3">Our Expertise</h2>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  TENSION was developed by a multidisciplinary team with expertise in:
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-neutral-50 rounded-lg text-center">
                <div className="text-2xl mb-2">🧠</div>
                <h4 className="font-medium mb-1">Clinical Psychology</h4>
                <p className="text-xs text-neutral-600">
                  Licensed therapists specializing in couples counseling
                </p>
              </div>

              <div className="p-4 bg-neutral-50 rounded-lg text-center">
                <div className="text-2xl mb-2">📊</div>
                <h4 className="font-medium mb-1">Behavioral Science</h4>
                <p className="text-xs text-neutral-600">
                  Researchers in attachment and relationship dynamics
                </p>
              </div>

              <div className="p-4 bg-neutral-50 rounded-lg text-center">
                <div className="text-2xl mb-2">💻</div>
                <h4 className="font-medium mb-1">Consumer AI</h4>
                <p className="text-xs text-neutral-600">
                  Experts in personalized behavioral interventions
                </p>
              </div>
            </div>
          </Card>

          {/* Privacy & Ethics */}
          <Card className="p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 rounded-full bg-red-100 dark:bg-red-900">
                <Shield className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-2xl font-medium mb-3">Privacy & Ethics</h2>
                <p className="text-neutral-700 leading-relaxed">
                  Relationship data is among the most sensitive personal information. We are committed to:
                </p>
                <ul className="mt-4 space-y-2 text-neutral-700">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>
                      Local-first relationship insights: check-ins and score history are designed to stay on your device
                      unless you export or enable optional cloud sync.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>
                      Strong transport security (TLS) for sign-in and synced data; passwords handled via Supabase Auth;
                      payments via Stripe—we never store full card numbers.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>We do not sell your personal data or use it for third-party ads.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>Clear Privacy Policy: what we collect, where it lives, and what you can control.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">✓</span>
                    <span>Export and account deletion paths so you can leave with your data or remove it.</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Disclaimer */}
          <Card className="border-amber-200 bg-amber-50 p-6 shadow-lg shadow-amber-900/10 dark:border-amber-800 dark:bg-amber-950/30">
            <h3 className="font-medium mb-2 text-amber-900 dark:text-amber-100">Important Disclaimer</h3>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              TENSION is a self-awareness tool, not a substitute for professional therapy, counseling,
              or clinical treatment. If you are experiencing relationship abuse, severe mental health
              issues, or crisis, please contact a licensed professional or crisis hotline immediately.
            </p>
          </Card>

          {/* Contact */}
          <div className="text-center py-8">
            <h3 className="font-medium mb-2">Questions or Feedback?</h3>
            <p className="text-sm text-neutral-600 mb-4">
              We'd love to hear from you.
            </p>
            <Button onClick={() => navigate('/settings')}>
              Contact Us
            </Button>
          </div>
        </div>
      </div>
    </WarmMarketingBackdrop>
  );
}
